import { Check, Edit, MoreHorizontal, Trash2 } from "lucide-react";

import { Badge } from "@workspace/ui/components/badge";
import {
	createColumnHelper,
	DataTable,
	type ColumnDef,
} from "@workspace/ui/components/basic-data-table";
import { Button } from "@workspace/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { FormattedDate } from "@workspace/ui/components/formatted-date";

import type { TaxItem } from "../types";

interface TaxItemsTableProps {
	data: TaxItem[];
	onEdit?: (taxItem: TaxItem) => void;
	onDelete?: (taxItem: TaxItem) => void;
	/** HA-232 AC8: offered on every rate but the standard one. */
	onMakeStandard?: (taxItem: TaxItem) => void;
	/** HA-232 AC7: the tenant's time zone, for the "Last updated" column (stored UTC). */
	timeZone?: string;
	isLoading?: boolean;
}

const columnHelper = createColumnHelper<TaxItem>();

const createTaxItemColumns = (
	{
		onEdit,
		onDelete,
		onMakeStandard,
		timeZone,
	}: Pick<
		TaxItemsTableProps,
		"onEdit" | "onDelete" | "onMakeStandard" | "timeZone"
	>,
	isLoading?: boolean,
): ColumnDef<TaxItem>[] =>
	[
		columnHelper.accessor((x) => x.label, {
			header: "Label",
			enableSorting: true,
			enableColumnFilter: false,
			cell: ({ cell, row }) => {
				return (
					<div className="flex items-center gap-2 font-medium">
						{cell.getValue<string>()}
						{row.original.isStandard && (
							<Badge variant="secondary">Standard</Badge>
						)}
					</div>
				);
			},
		}),
		columnHelper.accessor((x) => x.description, {
			header: "Description",
			enableSorting: true,
			enableColumnFilter: false,
			cell: ({ cell }) => {
				const description = cell.getValue<string | null>();
				return (
					<div className="text-sm text-muted-foreground">
						{description || "—"}
					</div>
				);
			},
		}),
		columnHelper.accessor((x) => x.rate, {
			header: "Tax Rate",
			enableSorting: true,
			enableColumnFilter: false,
			cell: ({ cell }) => {
				const rate = cell.getValue<{ value: number; currency: string }>();
				return (
					<div className="text-sm">
						{rate.value}% ({rate.currency})
					</div>
				);
			},
		}),
		columnHelper.accessor((x) => x.country, {
			header: "Country",
			enableSorting: true,
			enableColumnFilter: false,
			cell: ({ cell }) => {
				return <div className="text-sm">{cell.getValue<string>()}</div>;
			},
		}),
		columnHelper.accessor((x) => x.updatedAt, {
			header: "Last updated",
			enableSorting: true,
			enableColumnFilter: false,
			cell: ({ cell }) => (
				<div className="text-sm text-muted-foreground">
					<FormattedDate
						date={cell.getValue<string>()}
						timeZone={timeZone}
						options={{
							month: "short",
							day: "numeric",
							year: "numeric",
							hour: "2-digit",
							minute: "2-digit",
						}}
					/>
				</div>
			),
		}),
		columnHelper.display({
			id: "actions",
			header: "Actions",
			cell: ({ row }) => {
				const taxItem = row.original;
				const canMakeStandard = !!onMakeStandard && !taxItem.isStandard;

				return (
					<DropdownMenu>
						<DropdownMenuTrigger
							render={
								<Button
									variant="ghost"
									className="size-8 p-0"
									disabled={isLoading}
								>
									<span className="sr-only">Open menu</span>
									<MoreHorizontal className="h-4 w-4" />
								</Button>
							}
						/>
						<DropdownMenuContent align="end">
							<DropdownMenuLabel className="sr-only">Actions</DropdownMenuLabel>
							{onEdit && (
								<DropdownMenuItem
									onClick={() => onEdit(taxItem)}
									disabled={isLoading}
								>
									<Edit className="mr-2 size-4" />
									Edit Tax Item
								</DropdownMenuItem>
							)}
							{canMakeStandard && (
								<DropdownMenuItem
									onClick={() => onMakeStandard(taxItem)}
									disabled={isLoading}
								>
									<Check className="mr-2 size-4" />
									Make standard
								</DropdownMenuItem>
							)}
							{onDelete && (
								<DropdownMenuItem
									onClick={() => onDelete(taxItem)}
									disabled={isLoading}
									className="text-destructive focus:text-destructive"
								>
									<Trash2 className="mr-2 size-4" />
									Delete Tax Item
								</DropdownMenuItem>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				);
			},
		}),
	] as ColumnDef<TaxItem>[];

export function TaxItemsTable({
	data,
	onDelete,
	onEdit,
	onMakeStandard,
	timeZone,
	isLoading = false,
}: TaxItemsTableProps) {
	const anyAction = !!onEdit || !!onDelete || !!onMakeStandard;
	const columns = createTaxItemColumns(
		{ onEdit, onDelete, onMakeStandard, timeZone },
		isLoading,
	).filter((c) => anyAction || c.id !== "actions");

	return (
		<div className="space-y-4">
			<DataTable columns={columns} data={data} />
		</div>
	);
}
